import React from "react"
import styled from "styled-components"
import { graphql } from "gatsby"
import { useIntl } from "gatsby-plugin-react-intl"

import Layout from "../layouts/Layout"
import { Container, Row, Col } from "../components/Grid"
import BodyText from "../components/BodyText"
import Backlink from "../components/Backlink"
import PodcastPlayer from "../components/PodcastPlayer"
import Spacer from "../components/Spacer"
import Seo from "../components/Seo"
import StyledTeaser from "../components/Teaser"
import SocialShare from "../components/SocialShareVertical"
import Markdown from "../components/Markdown"

import { slugify } from "../helpers"
import { media, mixins } from "../styles"
import { headerHeightLg } from "../components/Header"

export const pageQuery = graphql`
  query PodcastDetailPageQuery($id: String, $language: String) {
    site {
      siteMetadata {
        siteUrl
        title
      }
    }
    contentfulPageMetadata(
      pagePath: { eq: "/podcast/" }
      node_locale: { eq: $language }
    ) {
      ...PageMetadata
    }
    podcastRssFeedEpisode(id: { eq: $id }) {
      ...PodcastData
    }
    podcastThumbnail: file(
      relativePath: { eq: "content/podcast/episode-thumbnail.png" }
    ) {
      childImageSharp {
        gatsbyImageData(width: 580, height: 350, layout: CONSTRAINED)
      }
    }
    relatedEpisodes: allPodcastRssFeedEpisode(
      filter: { id: { ne: $id } }
      sort: { item: { isoDate: DESC } }
      limit: 3
    ) {
      nodes {
        ...PodcastData
      }
    }
  }
`

const Wrapper = styled.div`
  background: ${({ theme }) => theme.greyLight};
  padding: 30px 0;

  @media ${media.md} {
    padding: 65px 0 5px;
  }

  @media ${media.xl} {
    padding: 90px 0 0;
  }
`

const ContentWidth = styled.div`
  margin: 0;

  @media ${media.md} {
    margin: 0 20px;
  }

  @media ${media.lg} {
    margin: 0 auto;
    max-width: 720px;
  }

  @media ${media.xl} {
    margin: 0 auto;
    max-width: 1024px;
  }
`

const MetaHeader = styled(ContentWidth)`
  display: flex;
  justify-content: center;

  @media ${media.sm} {
    margin-bottom: 20px;
  }

  h2 {
    ${mixins.title5}
    flex: 1;
    margin: 0 auto;
    text-align: center;
  }

  > span {
    flex: 1;
  }
`

const Header = styled.div`
  margin: 0 0 20px;
  padding: 15px 0 0;

  @media ${media.sm} {
    background: none;
    margin: 0 0 20px;
  }

  @media ${media.xl} {
    margin: 0 0 25px;
    padding: 35px 0 0;
  }
`

const BreadCrumb = styled.div`
  ${mixins.copy5};
  align-items: flex-start;
  color: ${({ theme }) => theme.sage};
  display: flex;
  margin: 0;
  flex-direction: column;
  flex: 1;
  text-align: center;

  @media ${media.md} {
    align-items: center;
    flex-direction: row;
  }
`

const PostTitle = styled.h1`
  ${mixins.title3}
  margin: 0 0 15px;

  @media ${media.xl} {
    margin: 0 0 25px;
  }
`

const PostBody = styled.div`
  background: ${({ theme }) => theme.white};
  padding: 30px 15px 40px;
  margin: 0 -15px;

  @media ${media.sm} {
    padding: 0;
    margin: 0;
  }

  @media print {
    padding-bottom: 0;
  }
`

const TextWidth = styled.div`
  max-width: 700px;
  margin: 0 auto;
  width: 100%;
`

const Content = styled(ContentWidth)`
  padding: 20px 0 60px;

  @media ${media.sm} {
    background: ${({ theme }) => theme.white};

    padding: 20px 30px 60px;
  }

  @media ${media.lg} {
    padding: 20px 35px 60px;
  }

  @media ${media.xl} {
    padding: 20px 50px 100px;
  }
`

const Share = styled.div`
  margin: 0 0 60px;

  @media ${media.lg} {
    margin: 0 0 30px;
    position: sticky;
    top: ${headerHeightLg + 20}px;
  }
`

const Related = styled(ContentWidth)`
  h3 {
    ${mixins.title5}
    color: ${({ theme }) => theme.sage};
    text-align: center;
    margin: 0 0 25px;

    @media ${media.xl} {
      margin: 0 0 65px;
    }
  }

  @media print {
    display: none;
  }
`

const RelatedHeader = styled.h3`
  ${mixins.title5}
  color: ${({ theme }) => theme.sage};
  text-align: center;
  margin: 0 0 25px;

  @media ${media.xl} {
    margin: 0 0 65px;
  }
`

const RelatedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;

  @media ${media.sm} {
    flex-flow: row wrap;
    justify-content: space-between;
  }
`

const RelatedTeaser = styled(StyledTeaser)`
  @media ${media.sm} {
    width: calc(50% - 15px);
  }

  @media ${media.md} {
    width: calc(33.3333333% - 20px);
  }
`

const PodcastPageTemplate = ({ data }) => {
  const pageMetadata = data.contentfulPageMetadata
  const { episode } = data.podcastRssFeedEpisode
  const relatedEpisodes = data.relatedEpisodes.nodes
  const podcastThumbnail = data.podcastThumbnail.childImageSharp.gatsbyImageData
  const intl = useIntl()

  const slug = `podcast/${slugify(episode.title)}`

  return (
    <Layout pageId={slug}>
      <Seo
        {...pageMetadata}
        title={`${episode.title} | ${pageMetadata.title}`}
        // description={episode.contentSnippet}
        socialShareImageOpengraph={`${data.site.siteMetadata.siteUrl}/assets/images/share/solarispodcastshareimage_og.png`}
        socialShareImageTwitter={`${data.site.siteMetadata.siteUrl}/assets/images/share/solarispodcastshareimage_tw.png`}
      />
      <Wrapper>
        <Container>
          <Row>
            <Col lg="10" lgOffset="1">
              <MetaHeader>
                <BreadCrumb>
                  <Backlink to={`/podcast/`} />
                </BreadCrumb>
                <h2>
                  {intl.formatMessage({
                    id: "knowledge.podcast",
                  })}
                </h2>
                <span />
              </MetaHeader>
            </Col>
          </Row>
          <Row>
            <Col lg="10" lgOffset="1">
              <Content>
                <Header>
                  <PostTitle>{episode.title}</PostTitle>
                </Header>
                <PostBody>
                  <Row>
                    <Col lg="9" lgOrder="2" xl="10">
                      <TextWidth>
                        <PodcastPlayer
                          date={episode.date}
                          key={episode.guid}
                          link={episode.link}
                          title={episode.title}
                        />
                        <BodyText>
                          <Markdown>{episode.content}</Markdown>
                        </BodyText>
                      </TextWidth>
                    </Col>
                    <Col lg="2" lgOrder="1" xl="1">
                      <Share>
                        <SocialShare
                          label={intl.formatMessage({ id: `podcast.share` })}
                          text={episode.title}
                          url={`${data.site.siteMetadata.siteUrl}/${intl.locale}/${slug}/`}
                        />
                      </Share>
                    </Col>
                  </Row>
                </PostBody>
              </Content>
              <Spacer height={50} xl={85} print={0} />
            </Col>
          </Row>
          {relatedEpisodes.length > 0 ? (
            <Row>
              <Col xl="8" xlOffset="2">
                <Related>
                  <RelatedHeader>
                    {intl.formatMessage({
                      id: "knowledge.furtherPodcastEpisodes",
                    })}
                  </RelatedHeader>
                  <RelatedList>
                    {relatedEpisodes.map(({ episode: relatedEpisode }) => (
                      <RelatedTeaser
                        key={relatedEpisode.guid}
                        to={`/podcast/${slugify(relatedEpisode.title)}/`}
                        header={relatedEpisode.title}
                        localImage={podcastThumbnail}
                        compact
                      />
                    ))}
                  </RelatedList>
                </Related>
                <Spacer height={20} md={55} />
              </Col>
            </Row>
          ) : null}
        </Container>
      </Wrapper>
    </Layout>
  )
}

export default PodcastPageTemplate
